import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, User, Calendar, Clock,
  Download, Printer, Send, CheckCircle, XCircle,
  Phone, Mail, MapPin, Package, IndianRupee, Edit2, CreditCard, Banknote, CheckCircle2
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrors';

const SalesInvoiceDetailPage = () => {
  const { businessId, id: invoiceId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_mode: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });

  useEffect(() => {
    if (bid && invoiceId) fetchInvoice();
  }, [bid, invoiceId]);

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/invoices/${invoiceId}`);
      const data = response.data;
      setInvoice(data);
      setPaymentData(prev => ({ ...prev, amount: data.balance_due || 0 }));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load invoice'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    // FINAL was missing -- caused finalized invoices to fall back to the
    // DRAFT badge and incorrectly read as "Draft" in the header. Same fix
    // already applied in the list page's getStatusBadge.
    const configs = {
      DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
      FINAL: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Final' },
      SENT: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
      PARTIALLY_PAID: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Partial' },
      PAID: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
      OVERDUE: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
      CANCELLED: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Cancelled' }
    };
    return configs[status] || configs.DRAFT;
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const handleFinalize = () => {
    setConfirmModal({
      title: 'Finalize Invoice',
      description: 'This will deduct stock and lock the invoice for editing. This cannot be undone.',
      confirmLabel: 'Finalize',
      confirmClass: 'bg-indigo-600 hover:bg-indigo-700',
      iconBgClass: 'bg-indigo-100',
      iconClass: 'text-indigo-600',
      icon: CheckCircle2,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.patch(`/invoices/${invoiceId}/finalize`);
          toast.success('Invoice finalized — stock updated');
          fetchInvoice();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error finalizing invoice'));
        }
      },
    });
  };

  const handleSend = () => {
    setConfirmModal({
      title: 'Mark Invoice as Sent',
      description: 'Invoice status will change to Sent. This cannot be undone.',
      confirmLabel: 'Mark as Sent',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      icon: Send,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.patch(`/invoices/${invoiceId}/send`);
          toast.success('Invoice marked as sent');
          fetchInvoice();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error sending invoice'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice?.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error downloading PDF'));
    }
  };

  // Record Payment keeps its form modal (it collects amount / mode / date /
  // reference -- that's data entry, not a yes/no confirmation). Only the
  // error path needed hardening.
  const handleRecordPayment = async () => {
    try {
      await api.post(`/invoices/${invoiceId}/payments`, paymentData);
      toast.success('Payment recorded');
      setShowPaymentModal(false);
      fetchInvoice();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error recording payment'));
    }
  };

  const handleCancel = () => {
    setConfirmModal({
      title: 'Cancel Invoice',
      description: 'This invoice will be cancelled. Stock will be restored if it was already finalized.',
      confirmLabel: 'Cancel Invoice',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      icon: XCircle,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.patch(`/invoices/${invoiceId}/cancel`);
          toast.success('Invoice cancelled');
          fetchInvoice();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error cancelling invoice'));
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-[13px] text-slate-500">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Invoice not found</p>
          <button onClick={() => navigate(`/business/${bid}/sales/invoices`)} className="mt-4 text-blue-600 hover:underline text-[14px]">
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(invoice.status);
  const items = invoice.items || invoice.line_items || [];

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageSlideIn">
      {/* ══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER - Commercial Grade Design
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* LEFT: Back Button + Title + Status */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/business/${bid}/sales/invoices`)}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[18px] font-semibold text-slate-800">{invoice.invoice_number}</h1>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                    {statusConfig.label}
                  </span>
                </div>
                <p className="text-[12px] text-slate-500 mt-0.5">Created {formatDate(invoice.created_at)}</p>
              </div>
            </div>
            
            {/* RIGHT: Action Buttons */}
            <div className="flex items-center gap-2">
              {invoice.status === 'DRAFT' && (
                <>
                  <button
                    onClick={() => navigate(`/business/${bid}/sales/invoices/${invoiceId}/edit`)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all text-[13px] font-medium"
                  >
                    <Edit2 className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={handleFinalize}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all text-[13px] font-medium"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Finalize
                  </button>
                </>
              )}
              {(invoice.status === 'DRAFT' || invoice.status === 'FINAL') && (
                <button
                  onClick={handleSend}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-[13px] font-medium"
                >
                  <Send className="w-4 h-4" /> Send
                </button>
              )}
              {['SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status) && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-[13px] font-medium"
                >
                  <CreditCard className="w-4 h-4" /> Record Payment
                </button>
              )}
              {!['PAID', 'CANCELLED'].includes(invoice.status) && (
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-all text-[13px] font-medium"
                >
                  <XCircle className="w-4 h-4" /> Cancel
                </button>
              )}
              <button
                onClick={handleDownloadPdf}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all text-[13px] font-medium"
              >
                <Download className="w-4 h-4" /> Download
              </button>
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-[13px] font-medium shadow-sm shadow-emerald-600/20"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN CONTENT - Premium 3-Column Layout
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT: Customer Info Card */}
            <div className="col-span-12 lg:col-span-3">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800">Customer</h3>
                    <p className="text-[11px] text-slate-500">Billing details</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-[15px] font-semibold text-slate-800">{invoice.customer_name || invoice.party_name_snapshot || '—'}</p>
                  </div>
                  {invoice.customer_phone && (
                    <div className="flex items-center gap-2 text-[13px] text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{invoice.customer_phone}</span>
                    </div>
                  )}
                  {invoice.customer_email && (
                    <div className="flex items-center gap-2 text-[13px] text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{invoice.customer_email}</span>
                    </div>
                  )}
                  {invoice.billing_address && (
                    <div className="flex items-start gap-2 text-[13px] text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span>{typeof invoice.billing_address === 'string' ? invoice.billing_address : [invoice.billing_address.line1, invoice.billing_address.line2, invoice.billing_address.city, invoice.billing_address.state, invoice.billing_address.pincode, invoice.billing_address.country].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Info Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200 mt-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800">Invoice Info</h3>
                    <p className="text-[11px] text-slate-500">Dates & payment terms</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Invoice Date</span>
                    <span className="font-medium text-slate-800">{formatDate(invoice.invoice_date)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Due Date</span>
                    <span className="font-medium text-slate-800">{formatDate(invoice.due_date)}</span>
                  </div>
                  {invoice.payment_terms && (
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-500">Payment Terms</span>
                      <span className="font-medium text-slate-800">{invoice.payment_terms}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Status Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200 mt-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <Banknote className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800">Payment Status</h3>
                    <p className="text-[11px] text-slate-500">Amount details</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Total Amount</span>
                    <span className="font-medium text-slate-800">{formatCurrency(invoice.grand_total)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Paid Amount</span>
                    <span className="font-medium text-emerald-600">{formatCurrency(invoice.amount_paid || 0)}</span>
                  </div>
                  <div className="h-px bg-slate-200 my-2"></div>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-semibold text-slate-800">Balance Due</span>
                    <span className={`text-[16px] font-bold ${(invoice.balance_due || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {formatCurrency(invoice.balance_due || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* CENTER: Items Table */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-sm">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-[14px] font-semibold text-slate-800">Invoice Items</h3>
                      <p className="text-[11px] text-slate-500">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Item</th>
                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Rate</th>
                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((item, index) => (
                        <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-[13px] font-medium text-slate-800">{item.item_name || item.name || item.product_name}</p>
                            {item.description && <p className="text-[11px] text-slate-500 mt-0.5">{item.description}</p>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-[13px] text-slate-700">{item.quantity} {item.unit || ''}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-[13px] text-slate-700">{formatCurrency(item.unit_price || item.rate || item.price)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-[13px] font-medium text-slate-800">{formatCurrency(item.amount || item.total || (item.quantity * (item.unit_price || item.rate || item.price)))}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* RIGHT: Summary Card */}
            <div className="col-span-12 lg:col-span-3">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800">Summary</h3>
                    <p className="text-[11px] text-slate-500">Invoice totals</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Subtotal</span>
                    <span className="font-medium text-slate-800">{formatCurrency(invoice.sub_total || invoice.subtotal)}</span>
                  </div>
                  {(invoice.discount_amount || invoice.discount) > 0 && (
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-500">Discount</span>
                      <span className="font-medium text-emerald-600">-{formatCurrency(invoice.discount_amount || invoice.discount)}</span>
                    </div>
                  )}
                  {(invoice.tax_amount || invoice.total_tax) > 0 && (
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-500">Tax</span>
                      <span className="font-medium text-slate-800">{formatCurrency(invoice.tax_amount || invoice.total_tax)}</span>
                    </div>
                  )}
                  <div className="h-px bg-slate-200 my-2"></div>
                  <div className="flex items-center justify-between">
                    <span className="text-[14px] font-semibold text-slate-800">Grand Total</span>
                    <span className="text-[18px] font-bold text-emerald-600">{formatCurrency(invoice.grand_total)}</span>
                  </div>
                </div>
              </div>

              {/* Notes Card */}
              {invoice.notes && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200 mt-4">
                  <h3 className="text-[14px] font-semibold text-slate-800 mb-3">Notes & Terms</h3>
                  <p className="text-[13px] text-slate-600 whitespace-pre-wrap">{invoice.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl animate-scaleIn">
            <h2 className="text-[18px] font-semibold text-slate-800 mb-4">Record Payment</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Amount</label>
                <input 
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[14px]"
                />
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Payment Mode</label>
                <select 
                  value={paymentData.payment_mode}
                  onChange={(e) => setPaymentData({...paymentData, payment_mode: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[14px]"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Payment Date</label>
                <input 
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[14px]"
                />
              </div>
              <div>
                <label className="block text-[12px] text-slate-500 mb-1">Reference</label>
                <input 
                  type="text"
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[14px]"
                  placeholder="Transaction ID, Cheque No, etc."
                />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-[13px] font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-[13px] font-medium"
              >
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconClass={confirmModal?.iconClass}
        iconBgClass={confirmModal?.iconBgClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default SalesInvoiceDetailPage;
